import { z } from 'zod'
import { type WithUserId } from '../user'
import { UTCMillisecondsSchema } from '../time'
import { SmallUserSchema } from '../smallUser'
import { TinyResidentSchema } from '../resident'
import { LatLngSchema, UpsertLatLngSchema } from '../locations/latLng'
import { WithImportIdSchema } from '../import'
import {
  AccountIdSchema,
  AttachmentIdSchema,
  CompanyIdSchema,
  idSchemaV1,
  InterviewIdSchema,
  isId,
  LocationIdSchema,
  TeamIdSchema,
  UserIdSchema,
  type UserId,
} from '../ids'
import { EventOutcomeIdSchema, SitOutcomeFaultSchema } from '../eventOutcome'
import { TinyTeamSchema } from '../entity'
import { DispositionIdSchema } from '../disposition'
import { type WithCompanyId } from '../company'
import { AuditPropsSchema } from '../common'
import { AddressSchema } from '../address'
import { TinyWorkflowAccountSchema } from '../account/model'

export const CalendarEventIdSchema = idSchemaV1('Event')
export type CalendarEventId = z.infer<typeof CalendarEventIdSchema>
export const isCalendarEventId = isId(CalendarEventIdSchema)

export const WithCalendarEventIdSchema = z.object({
  eventId: CalendarEventIdSchema,
})
export type WithCalendarEventId = z.infer<typeof WithCalendarEventIdSchema>

export function getEventType(value?: string): EventType | undefined {
  if (value === undefined) return
  const result = EventTypeSchema.safeParse(value)
  return result.success ? result.data : undefined
}

export const EVENT_TYPES = ['Consultation', 'Comeback', 'Interview', 'GeneralVisit', 'Shadow', 'Team'] as const
export const EventTypeSchema = z.enum(EVENT_TYPES).meta({
  id: 'eventType',
  description: 'Type of calendar event',
  example: 'Consultation',
})

export type EventType = z.infer<typeof EventTypeSchema>

export const SpecialtyAttachmentsSchema = z
  .object({
    utilityBill: AttachmentIdSchema.optional().meta({
      description: 'Utility bill attachment ID',
      example: 'Attachment:util123',
    }),
    customerVerification: AttachmentIdSchema.optional().meta({
      description: 'Customer verification attachment ID',
      example: 'Attachment:verify123',
    }),
  })
  .meta({
    description: 'Specialty attachments for calendar events',
  })
export type SpecialtyAttachments = z.infer<typeof SpecialtyAttachmentsSchema>

export const SyncedCalendarEventSchema = z.object({
  calendarType: z.string(),
  calendarId: z.string().meta({ description: 'ID of the synced device calendar' }),
  userId: UserIdSchema,
  deviceId: z.string().meta({ description: 'ID of the device that synced the event' }),
  eventId: z.string().meta({ description: 'ID of the event in the synced device calendar' }),
})
export type SyncedCalendarEvent = z.infer<typeof SyncedCalendarEventSchema>

export const CalendarEventLocationSchema = z
  .object({
    ...AddressSchema.pick({ oneLine: true, unitNbr: true }).shape,
    ...LatLngSchema.shape,
  })
  .meta({
    description: 'Location information for calendar events',
  })
export type CalendarEventLocation = z.infer<typeof CalendarEventLocationSchema>

export const UnsavedCalendarEventDataSchema = z
  .object({
    ...WithImportIdSchema.shape,
    eventType: EventTypeSchema,
    title: z.string().meta({
      id: 'calendarEventTitle',
      description: 'Event title',
      example: 'Customer Consultation',
    }),
    sourceId: z.string().optional().meta({
      id: 'calendarEventSourceId',
      description: 'External client system event ID',
    }),
    eventDate: UTCMillisecondsSchema,
    outcomeId: EventOutcomeIdSchema.optional().meta({
      id: 'calendarEventOutcomeId',
      description: 'ID for the Outcome of the event',
    }),
    dispositionId: DispositionIdSchema.optional(),
    duration: z.coerce.number().int().positive().meta({
      id: 'calendarEventDuration',
      description: 'Duration of event in minutes',
      example: 60,
    }),
    attachments: AttachmentIdSchema.array()
      .optional()
      .meta({
        description: 'Array of attachment IDs',
        example: ['Attachment:doc1', 'Attachment:doc2'],
      }),
    specialtyAttachments: SpecialtyAttachmentsSchema.optional(),
    accountId: AccountIdSchema.optional().meta({
      id: 'calendarEventAccountId',
      description: 'Associated account ID',
      example: 'Account:cust123',
    }),
    locationId: LocationIdSchema.optional().meta({
      id: 'calendarEventLocationId',
      description: 'Location ID',
      example: 'Location:home123',
    }),
    location: CalendarEventLocationSchema.optional(),
    ownerId: UserIdSchema.optional().meta({
      description:
        'Event owner user ID. Examples: Consultation: setter is owner, Shadow: rep is owner, Interview: manager is owner',
      example: 'U:owner123',
    }),
    attendeeId: UserIdSchema.optional().meta({
      id: 'calendarEventAttendeeId',
      description:
        'Event attendee user ID. Examples: Consultation: closer is attendee, Shadow: manager is attendee, Interview: rep is attendee',
      example: 'U:attendee123',
    }),
    dispatcherId: UserIdSchema.optional().meta({
      id: 'calendarEventDispatcherId',
      description: 'Dispatcher user ID',
      example: 'U:dispatcher123',
    }),
    occurredOwner: z.boolean().optional().meta({
      id: 'calendarEventOccurredOwner',
      description: 'Whether the owner attended the event',
    }),
    occurredAttendee: z.boolean().optional().meta({
      id: 'calendarEventOccurredAttendee',
      description: 'Whether the attendee attended the event',
    }),
    interviewId: InterviewIdSchema.optional().meta({
      id: 'calendarEventInterviewId',
      description: 'Associated interview ID',
      example: 'Interview:int123',
    }),
    notes: z.string().optional().meta({
      id: 'calendarEventNotes',
      description: 'Event notes. If using Workflows this will be migrated to account notes.',
      example: 'Customer was very interested in the product',
      deprecated: true,
    }),
    previousEventId: CalendarEventIdSchema.optional().meta({
      id: 'calendarEventPreviousId',
      description: 'Previous related event ID',
      example: 'Event:prev123',
    }),
    syncedCalendarIds: z.string().array().optional().meta({
      description: 'Synced calendar IDs (deprecated - use Google Calendar sync)',
      deprecated: true,
    }),
    syncedCalendarEvents: z.array(SyncedCalendarEventSchema).optional().meta({
      description: 'Synced calendar events (deprecated - use Google Calendar sync)',
      deprecated: true,
    }),
    teamId: TeamIdSchema.optional().meta({
      id: 'calendarEventTeamId',
      description: 'Associated team ID',
      example: 'Team:sales',
    }),
  })
  .meta({
    description: 'Calendar event data before persistence',
  })
export type UnsavedCalendarEventData = z.infer<typeof UnsavedCalendarEventDataSchema>

export const EventOutcomeHistorySchema = z
  .object({
    outcomeId: EventOutcomeIdSchema,
    userId: UserIdSchema,
    timestamp: UTCMillisecondsSchema.meta({
      description: 'When the outcome was recorded, in UTC epoch milliseconds',
      example: 1672531200000,
    }),
  })
  .meta({
    description: 'Historical record of event outcome changes',
  })
export type EventOutcomeHistory = z.infer<typeof EventOutcomeHistorySchema>

export const WithEventOutcomeHistorySchema = z.object({
  outcomeHistory: EventOutcomeHistorySchema.array(),
})
export type WithEventOutcomeHistory = z.infer<typeof WithEventOutcomeHistorySchema>

export const CalendarEventDataSchema = z
  .object({
    ...UnsavedCalendarEventDataSchema.shape,
    companyId: CompanyIdSchema.optional(),
    ...WithEventOutcomeHistorySchema.partial().shape,
    ...AuditPropsSchema.shape,
    eventId: CalendarEventIdSchema,
    ownerId: UserIdSchema,
    occurred: z.boolean().optional().meta({
      id: 'calendarEventOccurred',
      description: 'Whether this event occurred',
    }),
    closed: z.boolean().optional().meta({
      id: 'calendarEventClosed',
      description: 'Whether the account was closed during this event',
    }),
    reasonNotOccurred: z.string().optional().meta({
      id: 'calendarEventReasonNotOccurred',
      description: 'Reason the event did not occur (does not necessarily indicate that the event did not occur)',
      example: 'Customer rescheduled',
    }),
    fault: SitOutcomeFaultSchema.optional().meta({
      id: 'calendarEventFault',
      description: 'Fault information for the event',
      example: 'setter',
    }),
    allDay: z.boolean().optional().meta({
      id: 'calendarEventAllDay',
      description: 'Whether this is an all-day event',
    }),
    canDelete: z.boolean().optional().meta({
      id: 'calendarEventCanDelete',
      description: 'Whether the current user can delete this event',
    }),
  })
  .meta({
    description: 'Complete calendar event data with audit information',
  })
export type CalendarEventData = z.infer<typeof CalendarEventDataSchema>

export const CalendarEventDataWithDetailsSchema = z
  .object({
    ...CalendarEventDataSchema.shape,
    resident: TinyResidentSchema.optional().meta({
      description: 'Resident information (populated asynchronously)',
    }),
    owner: SmallUserSchema.optional().meta({
      description: 'Event owner details (populated from ownerId)',
    }),
    attendee: SmallUserSchema.optional().meta({
      description: 'Event attendee details (populated from attendeeId)',
    }),
    teams: z.array(TinyTeamSchema).optional().meta({
      description: 'Associated teams (populated asynchronously)',
    }),
    account: TinyWorkflowAccountSchema.optional().meta({
      description: 'Workflow Account information (populated asynchronously)',
    }),
  })
  .meta({
    description: 'Calendar event data with populated details (resident, owner, attendee, teams)',
  })

export type CalendarEventDataWithDetails = z.infer<typeof CalendarEventDataWithDetailsSchema>

export type WithEventType = Pick<CalendarEventData, 'eventType'>

export const AvailableDaySchema = z.object({
  day: z.string().meta({ description: 'Date string (yyyy-MM-dd)' }),
  available: z.boolean(),
})
export type AvailableDay = {
  /** Date string (yyyy-MM-dd) */
  day: string
  available: boolean
}

export type EventImportWithUserCompany = EventImport & WithUserId & WithCompanyId

export type EventImport = {
  raw: string
  line1?: string
  unitNbr?: string
  unitType?: string
  locality?: string
  countrySubd?: string
  postal1?: string
  latitude?: number
  longitude?: number
  title?: string
  eventType?: string
  eventDate?: number
  duration?: number
  setter?: UserId
  closer?: UserId
  outcome?: string
  notes?: string
}

export const OptionalCalendarEventLocationSchema = z
  .object({
    ...UpsertLatLngSchema.shape,
    locationId: LocationIdSchema.optional(),
    oneLine: z.string().optional(),
    unitNbr: AddressSchema.shape.unitNbr,
  })
  .meta({
    description: 'Location information for calendar events',
  })
export type OptionalCalendarEventLocation = z.infer<typeof OptionalCalendarEventLocationSchema>

export const CalendarEventRoleFilterSchema = z.enum(['owner', 'attendee']).meta({
  description: 'Type of user in relation to the calendar event',
})
export type CalendarEventRoleFilter = z.infer<typeof CalendarEventRoleFilterSchema>

export const SyncedCalendarSchema = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  default: z.boolean().meta({ deprecated: true }),
  color: z.string().optional(),
})
export type SyncedCalendar = z.infer<typeof SyncedCalendarSchema>

export const EventDedupeRequestSchema = z.object({
  fullrun: z.boolean(),
})
export type EventDedupeRequest = z.infer<typeof EventDedupeRequestSchema>
